import { forwardRef, useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import debouncePromise from 'debounce-promise';
import { Header, Grid } from 'semantic-ui-react';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';

import SearchableCreatableCard from 'componentsV2/SearchableCard/SearchableCreatableCard.component';
import { StyledSearchableCard, StyledClearButton } from '../../components/styles';
import AgreementModalInput from '../../components/ModalInput.component';
import { selectDealershipSettings } from 'services/DealershipSettings/selectors';
import { CardHeader } from 'views/Agreement/cards/AgreementCards.styles';
import UserIcon from 'assets/AgreementIcons/User';
import { PhoneFieldInput } from 'components/Inputs/Inputs.styles';
import colors from 'styles/colors';

import { query as querySearchCustomers } from 'services/Customers/resources';
import { usePrevious } from 'hooks';

export const emptyCustomer = {
  firstName: '',
  lastName: '',
  phoneCell: '',
};

const searchCustomers = (dealershipId: number | undefined) => {
  return debouncePromise((query) => {
    const urlParams = new URLSearchParams();
    // if we received a dealershipId, then we only want to query for customers belonging to that dealership
    if (dealershipId) {
      urlParams.append('dealership_scope', String(dealershipId));
    }
    // @ts-expect-error TS(2339): Property 'results' does not exist on type 'unknown'.
    return querySearchCustomers(10, 0, urlParams, query).then((response = { results: [] }) => response.results);
  }, 500);
};

type CustomerModalCardProps = {
  customer: any;
  setCustomer: (customer: any) => void;
  preventModification?: boolean;
  errors: any;
  setErrors: (errors: any) => void;
  isOpen?: boolean;
  dealershipId: number | null | undefined;
};

const CustomerModalCard = forwardRef<HTMLDivElement, CustomerModalCardProps>(
  ({ customer, setCustomer, preventModification = false, errors, setErrors, dealershipId }, ref) => {
    const { formatMessage } = useIntl();
    const prevDealershipId = usePrevious(dealershipId);

    const settings = useSelector(selectDealershipSettings());

    const [firstNameReadOnly, setFirstNameReadOnly] = useState(false);
    const [lastNameReadOnly, setLastNameReadOnly] = useState(false);
    const [phoneReadOnly, setPhoneReadOnly] = useState(false);
    const [baseCustomer, setBaseCustomer] = useState({});
    const [localValue, setLocalValue] = useState(customer?.phoneCell ?? '');
    const [isOptionSelected, setIsOptionSelected] = useState(customer?.id !== undefined);

    useEffect(() => {
      // This effects allows editing the phone number if it was not part of the user's info to begin with.
      // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
      if (typeof customer?.id === 'number' && customer.id !== baseCustomer.id) {
        setBaseCustomer(customer);

        const isValidNumber = Boolean(customer?.phoneCell) && isValidPhoneNumber(customer?.phoneCell);

        if (customer?.id && isValidNumber) {
          setPhoneReadOnly(true);
          setLocalValue(customer?.phoneCell);
        }
        if (customer?.id && customer?.firstName) {
          setFirstNameReadOnly(true);
        }
        if (customer?.id && customer?.lastName) {
          setLastNameReadOnly(true);
        }
      }
    }, [customer]);

    const handleClearCustomer = useCallback(() => {
      setCustomer(emptyCustomer);
      setBaseCustomer({});
      setPhoneReadOnly(false);
      setFirstNameReadOnly(false);
      setLastNameReadOnly(false);
      setLocalValue('');
      setIsOptionSelected(false);
      if (errors.createAgreement) {
        setErrors({ createAgreement: null });
      }
    }, [errors, setCustomer, setErrors]);

    useEffect(() => () => setCustomer(emptyCustomer), []); // Erase customer if modal is exited.

    // clear customer if the dealership is changed in the DealershipModalCard dropdown
    useEffect(() => {
      if (dealershipId !== prevDealershipId) {
        handleClearCustomer();
      }
    }, [dealershipId, prevDealershipId, handleClearCustomer]);

    return (
      <StyledSearchableCard>
        <SearchableCreatableCard
          ref={ref}
          header={
            <CardHeader $border>
              <UserIcon color={colors.fleetBlue} />
              {formatMessage({ id: 'views.Agreement.customer' })}
            </CardHeader>
          }
          // @ts-expect-error TS(2322): Type '{ children: Element; header: Element; title:... Remove this comment to see the full error message
          title={{ translationId: 'views.Agreement.customerInfo' }}
          onSelect={setCustomer}
          onSearch={searchCustomers(dealershipId)}
          isDisabled={preventModification}
          isOptionSelected={isOptionSelected}
          setIsOptionSelected={setIsOptionSelected}
        >
          {(customer.id || customer.id === null) && (
            <Grid columns={1}>
              <Grid.Row columns={1}>
                <Header as="h5">{formatMessage({ id: 'views.Agreement.contactDetails' })}</Header>
                {!preventModification && (
                  <StyledClearButton onClick={handleClearCustomer}>
                    {formatMessage({ id: 'agreementModal.clear' })}
                  </StyledClearButton>
                )}
              </Grid.Row>
              <Grid.Row columns={3}>
                <Grid.Column>
                  {/* @ts-expect-error TS(2739): Type '{ name: string; labelName: string; readOnly:... Remove this comment to see the full error message */}
                  <AgreementModalInput
                    name="customerFirstName"
                    labelName={formatMessage({ id: 'form.firstName' })}
                    readOnly={firstNameReadOnly}
                    value={customer?.firstName || ''}
                    onChange={(e) => setCustomer({ ...customer, firstName: e.target.value })}
                  />
                </Grid.Column>
                <Grid.Column>
                  {/* @ts-expect-error TS(2739): Type '{ name: string; labelName: string; readOnly:... Remove this comment to see the full error message */}
                  <AgreementModalInput
                    name="customerLastName"
                    labelName={formatMessage({ id: 'form.lastName' })}
                    readOnly={lastNameReadOnly}
                    value={customer?.lastName || ''}
                    onChange={(e) => setCustomer({ ...customer, lastName: e.target.value })}
                  />
                </Grid.Column>
                <Grid.Column>
                  {/* @ts-expect-error TS(2739): Type '{ name: string; labelName: string; readOnly:... Remove this comment to see the full error message */}
                  <AgreementModalInput
                    name="customerPhoneMobile"
                    labelName={formatMessage({ id: 'services.Customers.phoneMobile' })}
                    error={errors.phoneCell}
                    component={
                      <PhoneFieldInput
                        defaultCountry={settings.get('country').toLowerCase()}
                        disabled={phoneReadOnly}
                        onChange={(phone) => {
                          const phoneNumber = parsePhoneNumber(phone);
                          setCustomer({ ...customer, phoneCell: (phoneNumber && phoneNumber.number) || phone });
                          setLocalValue(phone);
                        }}
                        value={localValue}
                      />
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </SearchableCreatableCard>
      </StyledSearchableCard>
    );
  }
);

export default CustomerModalCard;
